import {TimePrecision} from '@mintdata/mint-kit/api/models/TimePrecision';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {BaseValue} from '@mintdata/mint-kit/api/values/BaseValue';
import {serializable} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import * as moment from 'moment';

const oneDayMilliseconds:number = 86400000;

export class ATime extends BaseValue
{
    @serializable public readonly hours:number;
    @serializable public readonly minutes:number;
    @serializable public readonly seconds:number;
    @serializable public readonly milliseconds:number;

    public static formats = [
        'LT', 'H:mm:ss', 'H:mm:ss.SSS', 'HH:mm:ss', 'HH:mm:ss.SSS',
        'H:mm:ssa', 'H:mm:ss a', 'H:mm:ss.SSSa', 'H:mm:ss.SSS a', 'HH:mm:ss a', 'HH:mm:ss.SSS a',
        'H:mm:ssA', 'H:mm:ss A', 'H:mm:ss.SSSA', 'H:mm:ss.SSS A', 'HH:mm:ss A', 'HH:mm:ss.SSS A',
        'HH:mm', 'H:mm', 'hh:mm', 'hh:mm:ss', 'hh:mm:ss.SSS',
        'h:mm', 'ha', 'hA', 'h:ma', 'h:mA', 'h a', 'h:m a',
        'hA', 'h:mA', 'h A', 'h:m A'
    ];

    public constructor(hours:number,
                       minutes:number,
                       seconds:number,
                       milliseconds:number)
    {
        super(Type.Time);

        this.hours = hours;
        this.minutes = minutes;
        this.seconds = seconds;
        this.milliseconds = milliseconds;
    }

    public toString():string
    {
        return this.toPreciseString(TimePrecision.millisecond());
    }

    public toPreciseString(precision:TimePrecision, useAMPM:boolean = false):string
    {
        let hours = this.hours;
        let modifier:string = '';

        if( useAMPM )
        {
            modifier = this.hours < 12 ? ' AM' : ' PM';
            hours = (this.hours % 12) || 12;
        }

        const padHours:string = `0${hours}`.slice(-2);
        const padMinutes:string = `0${this.minutes}`.slice(-2);
        const padSeconds:string = `0${this.seconds}`.slice(-2);
        const padMilliseconds:string = `00${this.milliseconds}`.slice(-3);

        if( precision.isEqualTo(TimePrecision.minute()) )
            return `${padHours}:${padMinutes}${modifier}`;

        if( precision.isEqualTo(TimePrecision.second()) )
            return `${padHours}:${padMinutes}:${padSeconds}${modifier}`;

        return `${padHours}:${padMinutes}:${padSeconds}.${padMilliseconds}${modifier}`;
    }

    public toValue():string
    {
        return `{"value":"${this.toString()}"}`;
    }

    public isEqualTo(other:IValue):boolean
    {
        other = other as ATime;

        return other &&
            Type.isInstanceOf(other, Type.Time) &&
            this.toString() === (other as ATime).toString();
    }

    public compareTo(other:IValue):number
    {
        if( !other ) return 1;

        if( Type.isInstanceOf(other, Type.Error) || Type.isInstanceOf(other, Type.Empty) )
            return 1;

        if( this.isEqualTo(other) )
            return 0;

        if( Type.isInstanceOf(other, Type.Time) )
            return this.toExcelTime() > (other as ATime).toExcelTime() ? 1 : -1;

        return -1;
    }

    public clone():IValue
    {
        return new ATime(this.hours, this.minutes, this.seconds, this.milliseconds);
    }

    public toExcelTime():number
    {
        const duration:moment.Duration = moment.duration({
            hours: this.hours,
            minutes: this.minutes,
            seconds: this.seconds,
            milliseconds: this.milliseconds
        });

        return duration.asMilliseconds() / oneDayMilliseconds;
    }

    public static fromExcelTime(time:number):ATime
    {
        const totalMilliseconds:number = Math.ceil(time * oneDayMilliseconds);
        const duration:moment.Duration = moment.duration(totalMilliseconds);

        return new ATime(
            duration.get('hours'),
            duration.get('minutes'),
            duration.get('seconds'),
            duration.get('milliseconds')
        );
    }

    public static fromString(value:string):ATime | AError
    {
        const m:moment.Moment = moment(value, ATime.formats, true);

        if( !m.isValid() )
            return new AError('Incorrect ATime format: ' + value);

        const date:Date = m.toDate();

        return new ATime(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
    }

    public static isValid(value:string):boolean
    {
        const m:moment.Moment = moment(value, ATime.formats, true);

        return m.isValid();
    }
}
